import { selectPlayerState } from "app/store/slices/player";
import { useAppSelector } from "app/store/store";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./podcast-player.module.scss";
import clsx from "clsx";
import { Category, PodcastChapter, Stream } from "services-hooks/types";
import { PodcastPlayer } from "./podcast-player.component";
import {
  useGetPillowListenV2ProgressChapter_id,
  useGetPillowPodcastchapterPlay,
} from "services-hooks/hooks";
import { useOfflineContext } from "app/context/offline.context";
import { useNetworkState } from "react-use";
import { notEmpty } from "app/ts";
import { selectUserState } from "app/store/slices/user";
import { useAudioAds } from "app/utils/audioAds";
import { usePlayerContext } from "app/context/player.context";

export const PodcastPlayerWrapper: React.FC = () => {
  const [playerOpen, setPlayerOpen] = useState(false);
  const { activeMedia } = useAppSelector(selectPlayerState);
  const activeChapter = activeMedia?.podcast?.podcastChapter;
  const podcast = activeMedia?.podcast?.podcast;

  return activeChapter ? (
    <>
      <div className={clsx(styles.wrapper)}>
        <PodcastStreamsLoader
          podcastChapter={activeChapter}
          podcast={podcast!}
          playerOpen={playerOpen}
          setPlayerOpen={setPlayerOpen}
        />
      </div>
    </>
  ) : null;
};

interface PodcastStreamsLoaderProps {
  podcastChapter: PodcastChapter;
  podcast: Category;
  playerOpen: boolean;
  setPlayerOpen: (open: boolean) => void;
}

const PodcastStreamsLoader: React.FC<PodcastStreamsLoaderProps> = ({
  podcast,
  playerOpen,
  setPlayerOpen,
  podcastChapter,
}) => {
  const {
    playerEnabled,
    fullscreenPlayerInitialyOpen,
    setFullscreenPlayerInitialyOpen,
  } = usePlayerContext();
  const { session } = useAppSelector(selectUserState);
  const networkState = useNetworkState();
  const { offlineChapters } = useOfflineContext();
  const {
    data,
    isLoading: streamsLoading,
    isError,
    refetch: refetchChapterPlay,
  } = useGetPillowPodcastchapterPlay(podcastChapter.permalink!, {
    enabled: false,
    keepPreviousData: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: chapterProgressResponse,
    isLoading: progressLoading,
    refetch: refetchChapterProgress,
  } = useGetPillowListenV2ProgressChapter_id(podcastChapter.permalink!, {
    enabled: false,
  });

  useEffect(() => {
    if (playerEnabled && fullscreenPlayerInitialyOpen) {
      setPlayerOpen(true);
      setFullscreenPlayerInitialyOpen(false);
    }
  }, [podcast.permalink, fullscreenPlayerInitialyOpen]);

  const offlineChapter = useMemo(() => {
    return offlineChapters?.find(
      (offlineChapter) => offlineChapter.permalink === podcastChapter.permalink
    );
  }, [offlineChapters, podcastChapter.permalink]);

  const offlineStream = useMemo(() => {
    if (offlineChapter) {
      const offlineStream: Stream = {
        url: URL.createObjectURL(offlineChapter?.file!),
        protocol: "offline",
      };
      return offlineStream;
    } else {
      return null;
    }
  }, [podcastChapter, offlineChapter?.chapter.permalink]);

  useEffect(() => {
    if (!offlineStream) {
      refetchChapterPlay();
      if (session?.user) {
        refetchChapterProgress();
      }
    }
  }, [offlineStream, networkState, podcastChapter, session]);

  const chapterProgress = useMemo(() => {
    if (offlineChapter) {
      return offlineChapter.playbackProgress;
    } else {
      return chapterProgressResponse?.data?.data?.finished
        ? 0
        : chapterProgressResponse?.data?.data?.seconds || 0;
    }
  }, [offlineChapter, chapterProgressResponse]);

  const chapterStreams = useMemo(
    () =>
      data?.data?.body?.content?.streams
        ?.filter(notEmpty)
        .sort(
          (streamA, streamB) => (streamB.bitrate || 0) - (streamA.bitrate || 0)
        ) || [],
    [data]
  );

  const {
    adStreams,
    adStreamsLoading,
    sendTargetspotTimeUpdateEvent,
    sendTrackingEvent,
    sendTargetspotStateUpdateEvent,
  } = useAudioAds(podcast);

  return (
    <PodcastPlayer
      podcastChapter={podcastChapter}
      podcast={podcast!}
      streamsLoadError={isError}
      refetchStreams={refetchChapterPlay}
      playerOpen={playerOpen}
      setPlayerOpen={setPlayerOpen}
      streams={offlineStream ? [offlineStream] : chapterStreams}
      adStreams={adStreams}
      streamsLoading={
        offlineStream
          ? false
          : progressLoading || adStreamsLoading || streamsLoading
      }
      sendTargetspotTimeUpdateEvent={sendTargetspotTimeUpdateEvent}
      sendTargetspotTrackingEvent={sendTrackingEvent}
      sendTargetspotStateUpdateEvent={sendTargetspotStateUpdateEvent}
      initialProgress={chapterProgress}
      isOffline={!!offlineChapter}
    />
  );
};

export default PodcastPlayerWrapper;
